import React, { useRef, useState } from "react";
import {
  uploadToCloudinary,
  CloudinaryFolder,
  MAX_UPLOAD_BYTES,
  isPdfCloudinaryUrl,
  isPdfFile,
} from "../../utils/cloudinary";
import { toast } from "react-toastify";
import { Colors } from "../../utils/utils.colors";
import { FiUpload, FiX, FiFile } from "react-icons/fi";

const UploadI = FiUpload as any;
const XI = FiX as any;
const FileI = FiFile as any;

type Props = {
  value: string;
  onChange: (url: string) => void;
  folder?: CloudinaryFolder;
  label?: string;
  testId?: string;
};

const maxMb = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));

/** Upload images or PDF documents to Cloudinary. */
export const DocumentUpload: React.FC<Props> = ({
  value,
  onChange,
  folder = "ulpgl/uploads",
  label = "Document",
  testId = "document-upload",
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file: File) => {
    const ok = file.type.startsWith("image/") || isPdfFile(file);
    if (!ok) return toast.warn("Formats acceptés : PDF, PNG, JPG, WEBP");
    if (file.size > MAX_UPLOAD_BYTES) {
      return toast.warn(`Fichier trop volumineux (max ${maxMb} Mo)`);
    }
    setBusy(true);
    try {
      const url = await uploadToCloudinary(file, folder);
      onChange(url);
      toast.success("Fichier téléversé");
    } catch (e: any) {
      console.error("[DocumentUpload]", e);
      toast.error(e?.message || "Erreur d'upload");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: "#444", marginBottom: 6, fontWeight: 500 }}>{label}</div>
      {value ? (
        <div style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
          {isPdfCloudinaryUrl(value) ? (
            <div
              data-testid={`${testId}-preview`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: 16,
                border: "1px solid #ddd",
                borderRadius: 8,
                background: "#f8fafc",
                minWidth: 280,
              }}
            >
              <FileI size={32} color={Colors.primaryColor} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Document PDF</div>
                <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: Colors.primaryColor, wordBreak: "break-all" }}>
                  Ouvrir le fichier
                </a>
              </div>
            </div>
          ) : (
            <img
              src={value}
              alt=""
              data-testid={`${testId}-preview`}
              style={{ width: "100%", maxWidth: 320, height: 180, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }}
            />
          )}
          <button
            data-testid={`${testId}-remove`}
            type="button"
            onClick={() => onChange("")}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "rgba(0,0,0,0.6)",
              color: "white",
              border: "none",
              width: 30,
              height: 30,
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <XI />
          </button>
        </div>
      ) : (
        <div
          data-testid={`${testId}-dropzone`}
          onClick={() => inputRef.current?.click()}
          style={{
            border: "2px dashed #c9ccd6",
            padding: 26,
            borderRadius: 8,
            cursor: "pointer",
            background: busy ? "#f5f5f5" : "white",
            textAlign: "center",
            color: "#666",
          }}
        >
          <UploadI size={28} color={Colors.primaryColor} />
          <div style={{ marginTop: 8, fontSize: 14 }}>
            {busy ? "Téléversement…" : "PDF ou image — cliquez ou glissez"}
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>PDF, PNG, JPG, WEBP — max {maxMb} Mo</div>
        </div>
      )}
      <input
        data-testid={`${testId}-input`}
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};
